import React, { useState, useEffect } from 'react';
import { 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  TextField, 
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Paper,
  Button,
  Tooltip
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { SortOption, FilterOptions, Tag } from '../types';
import { api } from '../services/api';

interface FilterControlsProps {
  onFilterChange: (filters: FilterOptions) => void;
  onSortChange: (sort: SortOption) => void;
  currentSort: SortOption;
  currentFilters: FilterOptions;
}

const FilterControls: React.FC<FilterControlsProps> = ({
  onFilterChange,
  onSortChange,
  currentSort,
  currentFilters,
}) => {
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>(currentFilters.searchTerm || '');

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const tags = await api.getTags();
        setAvailableTags(tags);
      } catch (error) {
        console.error('Failed to fetch tags:', error);
      }
    };

    fetchTags();
  }, []);

  const handleSortChange = (event: SelectChangeEvent) => {
    onSortChange(event.target.value as SortOption);
  };

  const handleTagsChange = (event: SelectChangeEvent<string[]>) => {
    const selectedTagIds = event.target.value as string[];
    onFilterChange({ ...currentFilters, tags: selectedTagIds });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    // Debounce this in a real app
    onFilterChange({ ...currentFilters, searchTerm: value });
  };

  const handleClearAllTags = () => {
    onFilterChange({ ...currentFilters, tags: [] });
  };

  // Find tag objects by their IDs
  const getTagsByIds = (tagIds: string[]): Tag[] => {
    return availableTags.filter(tag => tagIds.includes(tag.id));
  };

  // Get selected tag objects based on their IDs
  const selectedTags = getTagsByIds(currentFilters.tags);

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        p: 2, 
        mb: 3,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        gap: 2
      }}
    >
      <FormControl 
        sx={{ flex: 1, minWidth: { xs: '100%', md: '180px' } }}
      >
        <InputLabel id="sort-select-label">Sort By</InputLabel>
        <Select
          labelId="sort-select-label"
          id="sort-select"
          value={currentSort}
          label="Sort By"
          onChange={handleSortChange}
        >
          <MenuItem value="mostUpvoted">Most Upvoted</MenuItem>
          <MenuItem value="mostRecent">Most Recent</MenuItem>
        </Select>
      </FormControl>
      
      <FormControl 
        sx={{ flex: 2, minWidth: { xs: '100%', md: '200px' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
          <Box sx={{ flex: 1, width: '100%' }}>
            <InputLabel id="tags-filter-label">Filter by Tags</InputLabel>
            <Select
              labelId="tags-filter-label"
              id="tags-filter"
              multiple
              value={currentFilters.tags}
              onChange={handleTagsChange}
              input={<OutlinedInput label="Filter by Tags" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selectedTags.map((tag) => (
                    <Chip key={tag.id} label={tag.name} size="small" />
                  ))}
                </Box>
              )}
              sx={{ width: '100%' }}
            >
              {availableTags.map((tag) => (
                <MenuItem key={tag.id} value={tag.id}>
                  {tag.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
          
          {currentFilters.tags.length > 0 && (
            <Tooltip title="Clear all tags">
              <Button 
                onClick={handleClearAllTags}
                color="secondary"
                sx={{ ml: 1, mt: 1, minWidth: 'auto' }}
                variant="outlined"
                size="small"
                startIcon={<ClearIcon />}
              >
                Clear
              </Button>
            </Tooltip>
          )}
        </Box>
      </FormControl>
      
      <TextField
        label="Search"
        variant="outlined"
        value={searchTerm}
        onChange={handleSearchChange}
        sx={{ flex: 1, minWidth: { xs: '100%', md: '180px' } }}
      />
    </Paper>
  );
};

export default FilterControls; 